import { Component, inject } from '@angular/core';
import * as XLSX from 'xlsx';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { BoRolesCheckService } from "../../../../components/rolesCheck/bo-roles-check.service";
import {
  asientosHeaders,
  clientesHeaders,
  planHeaders,
  convertAsientos,
  convertClientes,
  convertPlan,
  ExclusionStats,
} from "./holded-to-sage-utils";
import { WorkSheet } from "xlsx";
import { HoldedToSageError } from "./holded-to-sage-error";
import { ConversionErrorDialogComponent2025 } from "./conversion-error-dialog.component";

@Component({
  selector: 'app-holded-to-sage',
  templateUrl: './holded-to-sage.component.html',
  styleUrls: ['./holded-to-sage.component.scss']
})
export class BOHoldedToSageComponent2025 {
  rolesCheck = inject(BoRolesCheckService);
  private dialog = inject(MatDialog);
  private _snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);

  asientosFile: File | null = null;
  clientesFile: File | null = null;
  planFile: File | null = null;

  asientosHeaderClue: string = "Entry";
  clientesHeaderClue: string = "Created";
  planHeaderClue: string = "Account";

  isLoading: boolean = false;
  rejectedClientes: Array<{ reason: string, data: Record<string, any> }> = [];
  rejectedAsientos: Array<{ reason: string, data: Record<string, any> }> = [];
  asientosExclusionStats: ExclusionStats | null = null;

  async ngOnInit() {
    const canAccess = await this.rolesCheck.checkAccess();
    if (!canAccess) {
      return;
    }
  }

  onFileChange(event: Event, tipo: 'asientos' | 'clientes' | 'plan'): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      const file = input.files[0];
      if (tipo === 'asientos') this.asientosFile = file;
      if (tipo === 'clientes') this.clientesFile = file;
      if (tipo === 'plan') this.planFile = file;
    }
  }

  async convertFiles(): Promise<void> {
    if (!this.clientesFile && !this.asientosFile && !this.planFile) {
      this._snackBar.open(
        this.translate.instant('BACKOFFICE.HOLDED_TO_SAGE.ERRORS.NO_FILES_SELECTED'),
        'Cerrar',
        { duration: 3000 }
      );
      return;
    }

    this.isLoading = true;
    this.rejectedClientes = []; // Limpiar registros rechazados previos
    this.rejectedAsientos = [];
    const wb = XLSX.utils.book_new();
    const processedFiles: string[] = [];
    let hasErrors = false;
    let totalProcessed = 0;
    let totalRejected = 0;

    try {
      // Intentar procesar cada archivo con manejo individual de errores

      if (this.clientesFile) {
        try {
          const stats = await this.processClientesFile(wb);
          processedFiles.push('Clientes');
          totalProcessed += stats.processed;
          totalRejected += stats.rejected;
        } catch (error) {
          hasErrors = true;
          const shouldContinue = await this.handleConversionError(error as HoldedToSageError, 'clientes');
          if (!shouldContinue) {
            return; // Cancelar todo
          }
        }
      }

      if (this.asientosFile) {
        try {
          const stats = await this.processAsientosFile(wb);
          processedFiles.push('Asientos');
          totalProcessed += stats.processed;
          totalRejected += stats.rejected;
        } catch (error) {
          hasErrors = true;
          const shouldContinue = await this.handleConversionError(error as HoldedToSageError, 'asientos');
          if (!shouldContinue) {
            return;
          }
        }
      }

      if (this.planFile) {
        try {
          await this.processPlanFile(wb);
          processedFiles.push('Plan contable');
        } catch (error) {
          hasErrors = true;
          const shouldContinue = await this.handleConversionError(error as HoldedToSageError, 'plan');
          if (!shouldContinue) {
            return;
          }
        }
      }

      // Si se procesó al menos un archivo, generar Excel
      if (processedFiles.length > 0) {
        this.generateAndDownloadExcel(wb);

        let message = hasErrors
          ? this.translate.instant('BACKOFFICE.HOLDED_TO_SAGE.ERRORS.PARTIAL_SUCCESS', { processed: processedFiles.join(', ') })
          : this.translate.instant('BACKOFFICE.HOLDED_TO_SAGE.ERRORS.SUCCESS');

        // Agregar información de registros procesados/rechazados
        if (totalProcessed > 0 || totalRejected > 0) {
          message += ` (${totalProcessed} procesados, ${totalRejected} rechazados)`;
        }

        this._snackBar.open(message, 'Cerrar', {
          duration: 5000,
          panelClass: hasErrors || totalRejected > 0 ? ['warning-snackbar'] : ['success-snackbar']
        });
      } else {
        this._snackBar.open(
          this.translate.instant('BACKOFFICE.HOLDED_TO_SAGE.ERRORS.NO_FILES_PROCESSED'),
          'Cerrar',
          { duration: 5000, panelClass: ['error-snackbar'] }
        );
      }
    } finally {
      this.isLoading = false;
    }
  }

  private readExcelFile(file: File, headerClue: string, fileType: 'asientos' | 'clientes' | 'plan'): Promise<any[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e: any) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows: any[] = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
          const rowStart = this.getInitialRow(worksheet, headerClue);

          if (rowStart === -1) {
            const sheetPreview = rows.slice(0, 10);
            console.error('❌ Error en readExcelFile:', {
              fileType,
              fileName: file.name,
              headerClue,
              message: `No se encontró la cabecera "${headerClue}" en el archivo`,
              totalRows: rows.length,
              sheetPreview,
              // Análisis detallado de las primeras filas
              rowAnalysis: rows.slice(0, 10).map((row, index) => ({
                rowIndex: index,
                cellCount: row.length,
                nonEmptyCells: row.filter((c: any) => c !== null && c !== undefined && c !== '').length,
                firstFiveCells: row.slice(0, 5).map((c: any) => ({
                  value: c,
                  type: typeof c,
                  asString: String(c || '')
                }))
              }))
            });

            reject(new HoldedToSageError(
              fileType,
              file.name,
              headerClue,
              `Header "${headerClue}" not found`,
              sheetPreview
            ));
          } else {
            const range = XLSX.utils.decode_range(worksheet['!ref']!);
            range.s.r = rowStart;
            if (range.e.r > rowStart) {
              range.e.r -= 1;
            }
            const adjustedRange = XLSX.utils.encode_range(range);

            const json = XLSX.utils.sheet_to_json(worksheet, {
              range: adjustedRange,
              defval: ''
            });

            resolve(json);
          }
        } catch (err) {
          console.error('❌ Error leyendo archivo Excel:', {
            fileType,
            fileName: file.name,
            error: err
          });
          reject(new HoldedToSageError(
            fileType,
            file.name,
            headerClue,
            err,
            []
          ));
        }
      };

      reader.onerror = (err) => {
        console.error('❌ Error en FileReader:', {
          fileType,
          fileName: file.name,
          error: err
        });
        reject(new HoldedToSageError(
          fileType,
          file.name,
          headerClue,
          err,
          []
        ));
      };
      reader.readAsArrayBuffer(file);
    });
  }

  private getInitialRow(sheet: WorkSheet, headerClue: string): number {
    const rows: any[] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });

    // Buscar solo en las primeras 20 filas (seguridad)
    // En Holded, las cabeceras están típicamente entre fila 0-6
    const searchLimit = Math.min(20, rows.length);

    // Normalizar headerClue para comparación
    const normalizedHeaderClue = String(headerClue).toLowerCase().trim();

    for (let i = 0; i < searchLimit; i++) {
      const row = rows[i];

      // Buscar en cada celda de la fila
      for (let j = 0; j < row.length; j++) {
        const cell = row[j];

        // Convertir a string y normalizar
        const normalizedCell = String(cell || '').toLowerCase().trim();

        // Comparar
        if (normalizedCell === normalizedHeaderClue) {
          console.log(`✅ Cabecera "${headerClue}" encontrada en fila ${i}, columna ${j}`);
          console.log(`   Contenido de la fila:`, row);
          return i;
        }
      }
    }

    // No encontrado - logging detallado
    console.error(`❌ Cabecera "${headerClue}" NO encontrada en las primeras ${searchLimit} filas`);
    console.error('   Primeras 10 filas del archivo:', rows.slice(0, 10));

    return -1;
  }

  private getExcelFilename(): string {
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');

    const year = now.getFullYear();
    const month = pad(now.getMonth() + 1);
    const day = pad(now.getDate());
    const hours = pad(now.getHours());
    const minutes = pad(now.getMinutes());
    const seconds = pad(now.getSeconds());

    return `DC_Diario_${year}${month}${day}_${hours}${minutes}${seconds}.xlsx`;
  }

  private excelSerialToDate(serial: any): string {
    if (!serial) return 'Sin fecha';

    // Si ya es una cadena de texto que parece una fecha, devolverla
    if (typeof serial === 'string' && !isNaN(Number(serial)) === false) {
      return serial;
    }

    // Convertir a número si es necesario
    const serialNumber = typeof serial === 'number' ? serial : parseFloat(serial);

    if (isNaN(serialNumber)) return 'Sin fecha';

    // Excel almacena las fechas como días desde 1/1/1900
    // Nota: Excel tiene un bug de año bisiesto 1900, pero lo ignoramos para simplificar
    const excelEpoch = new Date(1899, 11, 30); // 30 de diciembre de 1899
    const days = Math.floor(serialNumber);
    const milliseconds = days * 24 * 60 * 60 * 1000;
    const date = new Date(excelEpoch.getTime() + milliseconds);

    // Formatear la fecha como DD/MM/YYYY
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  }

  // Método público para usar en el template
  formatExcelDate(serial: any): string {
    return this.excelSerialToDate(serial);
  }

  // Método para obtener el nombre limpio (sin teléfono si existe)
  getDisplayName(nameField: string): string {
    if (!nameField) return 'Sin nombre';
    return nameField;
  }

  private async processClientesFile(wb: XLSX.WorkBook): Promise<{ processed: number, rejected: number }> {
    const json = await this.readExcelFile(
      this.clientesFile!,
      this.clientesHeaderClue,
      'clientes'
    );
    console.log('✅ Clientes leídos:', json.length, 'registros');

    const { convertedData, rejectedRecords } = convertClientes(json);
    console.log('✅ Clientes convertidos:', convertedData.length, 'registros');
    console.log('⚠️ Clientes rechazados:', rejectedRecords.length, 'registros');

    // Acumular registros rechazados para mostrar en UI
    if (rejectedRecords.length > 0) {
      this.rejectedClientes = rejectedRecords;
    }

    const newSheet = XLSX.utils.json_to_sheet(convertedData, {
      header: clientesHeaders
    });
    XLSX.utils.book_append_sheet(wb, newSheet, 'ClientesProveedores');

    return { processed: convertedData.length, rejected: rejectedRecords.length };
  }

  private async processAsientosFile(wb: XLSX.WorkBook): Promise<{ processed: number, rejected: number }> {
    const json = await this.readExcelFile(
      this.asientosFile!,
      this.asientosHeaderClue,
      'asientos'
    );
    console.log('✅ Asientos leídos:', json.length, 'registros');

    const { convertedData, rejectedRecords, exclusionStats } = convertAsientos(json);
    console.log('✅ Asientos convertidos:', convertedData.length, 'registros');
    console.log('⚠️ Asientos excluidos:', exclusionStats.total, 'registros');

    // Guardar estadísticas para mostrar en UI
    this.asientosExclusionStats = exclusionStats;

    // Acumular registros rechazados para mostrar en UI (si aplica en el futuro)
    if (rejectedRecords.length > 0) {
      this.rejectedAsientos = rejectedRecords;
    }

    const newSheet = XLSX.utils.json_to_sheet(convertedData, {
      header: asientosHeaders
    });

    // Aplicar formato de fecha a la columna FechaAsiento (columna D, índice 3)
    const range = XLSX.utils.decode_range(newSheet['!ref'] || 'A1');
    for (let row = range.s.r + 1; row <= range.e.r; row++) {
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: 3 }); // Columna D (FechaAsiento)
      if (newSheet[cellAddress] && typeof newSheet[cellAddress].v === 'number') {
        // Solo aplicar formato, NO cambiar el tipo a 'd' para evitar conversión errónea
        newSheet[cellAddress].z = 'm/d/yy'; // Formato: mes/día/año (sin ceros, 2 dígitos año)
      }
    }

    XLSX.utils.book_append_sheet(wb, newSheet, 'DC_Diario');

    return { processed: convertedData.length, rejected: rejectedRecords.length };
  }

  private async processPlanFile(wb: XLSX.WorkBook): Promise<void> {
    const json = await this.readExcelFile(
      this.planFile!,
      this.planHeaderClue,
      'plan'
    );
    console.log('✅ Plan contable leído:', json.length, 'registros');

    const convertedJson = convertPlan(json);
    console.log('✅ Plan contable convertido:', convertedJson.length, 'registros');

    const newSheet = XLSX.utils.json_to_sheet(convertedJson, {
      header: planHeaders
    });
    XLSX.utils.book_append_sheet(wb, newSheet, 'Plan cuentas');
  }

  private generateAndDownloadExcel(wb: XLSX.WorkBook): void {
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = this.getExcelFilename();
    anchor.click();
    window.URL.revokeObjectURL(url);
  }

  private async handleConversionError(
    error: HoldedToSageError,
    fileType: 'asientos' | 'clientes' | 'plan'
  ): Promise<boolean> {
    // Logging detallado en consola
    console.error('❌ Error en conversión:', {
      fileType: error.fileType,
      fileName: error.fileName,
      headerExpected: error.headerClue,
      error: error.originalError,
      sheetPreview: error.sheetPreview
    });

    // Determinar archivos restantes
    const remainingFiles = this.getRemainingFiles(fileType);

    if (remainingFiles.length === 0) {
      // No hay más archivos, solo mostrar error
      this._snackBar.open(
        this.translate.instant('BACKOFFICE.HOLDED_TO_SAGE.ERRORS.HEADER_NOT_FOUND', {
          headerClue: error.headerClue,
          fileName: error.fileName
        }),
        'Cerrar',
        { duration: 5000, panelClass: ['error-snackbar'] }
      );
      return false;
    }

    // Hay archivos restantes, preguntar al usuario
    const dialogRef = this.dialog.open(ConversionErrorDialogComponent2025, {
      width: '500px',
      data: {
        title: this.translate.instant('BACKOFFICE.HOLDED_TO_SAGE.ERRORS.PARTIAL_CONVERSION_TITLE'),
        message: this.translate.instant('BACKOFFICE.HOLDED_TO_SAGE.ERRORS.PARTIAL_CONVERSION_MESSAGE', {
          failedFile: fileType
        }),
        details: `Archivo: ${error.fileName}\nCabecera esperada: "${error.headerClue}"\nError: ${error.originalError}`
      }
    });

    const result = await firstValueFrom(dialogRef.afterClosed());
    return result === true; // true = continuar, false/undefined = cancelar
  }

  private getRemainingFiles(failedFile: 'asientos' | 'clientes' | 'plan'): string[] {
    const remaining: string[] = [];

    // Orden de procesamiento: clientes -> asientos -> plan
    if (failedFile === 'clientes') {
      if (this.asientosFile) remaining.push('asientos');
      if (this.planFile) remaining.push('plan');
    } else if (failedFile === 'asientos') {
      if (this.planFile) remaining.push('plan');
    }
    // Si falla 'plan', no hay archivos restantes

    return remaining;
  }
}
